import React from 'react';

interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  subMessage?: string;
}

/**
 * A full-screen loading overlay with animated spinner.
 * Use this for long-running operations like file imports, AI processing, etc.
 *
 * Features:
 * - Semi-transparent backdrop
 * - Animated spinning circle
 * - Customizable primary message and sub-message
 * - Dark mode compatible
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  message = 'Processing...',
  subMessage,
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Content */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-sm mx-4 text-center">
        {/* Spinner */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            {/* Outer ring */}
            <div className="w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700" />
            {/* Spinning arc */}
            <div className="absolute top-0 left-0 w-16 h-16 rounded-full border-4 border-transparent border-t-amber-500 animate-spin" />
            {/* Inner pulse */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 animate-pulse" />
          </div>
        </div>

        {/* Message */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {message}
        </h3>

        {/* Sub-message */}
        {subMessage && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {subMessage}
          </p>
        )}

        {/* Animated dots */}
        <div className="flex justify-center mt-4 space-x-1">
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
};

export default LoadingOverlay;
